import type { Category, LayerId, LieType, Side } from "../board/types";

export type CardKind = "instant" | "placeable";

/**
 * How drag intent is applied.
 * Click always uses pure auto rules (no intent).
 */
export type RespectMask =
  | "exactSlot"
  | "layerOnly"
  | "layerPrefer"
  | "autoOnly"
  | "adjacentExpand";

export type InstantSelector =
  | "strongestLie"
  | "innermostLie"
  | "typePriority"
  | "layerSlam"
  /**
   * Chip every (matching) lie on one layer by a flat answerLevels —
   * never full-clear on meet. Swarm tool; weak on empty layers.
   */
  | "layerChip"
  | "self"
  /** Convert a lie into a Prayer on the same slot */
  | "convertLie"
  /** Slow advance windup on a lie or layer */
  | "slowLie"
  | "slowLayer"
  /**
   * Empower: next Answer/Chip (in hand or drawn later) gains +answer step(s).
   * No board target — always resolves if charges can be granted.
   */
  | "empower"
  /**
   * Restore Keep: return a damaged Prayer to its maxLevel (clear wear).
   * Fails (card stays in hand) if no Prayer is below max.
   */
  | "restorePrayer"
  /**
   * Gather: drag a Prayer below maxLevel; other residual (I) Prayers feed it
   * (+1 level each, up to capacity = maxLevel − level). Never raises maxLevel.
   * Fails if host full or no other I donors.
   */
  | "gatherPrayers";

export interface VerbKit {
  /** Wear opposing presence in same layer (contest rate) */
  contest?: number;
  /**
   * When true with `contest`, wear foes on the *next* layer in advance direction
   * instead of this layer (player → outward toward Mind; enemy → inward).
   * Path prayers use this to clear the way ahead.
   */
  contestNextLayer?: boolean;
  /** Push layer meter: positive = Lie, negative = Truth */
  pressure?: number;
  /** Resists wear / overflow */
  guard?: boolean;
  /**
   * Hunt keeps: on spawn, hammer a Prayer on the target layer before
   * taking an empty gap. Breaks prayer-blanket stalemates.
   */
  siegeFirst?: boolean;
  /**
   * Seconds of visible windup before this lie strikes the Secret Place.
   * Any player Answer / Chip / Soften / Still on it rewinds the charge.
   */
  taxAfter?: number;
  /** Integrity dealt when `taxAfter` windup completes (default ~12). */
  taxHit?: number;
  /**
   * Seconds of settled conditions before advancing one layer:
   * enemy → inward (toward Secret Place); player Prayer → outward (toward Mind).
   */
  advanceAfter?: number;
  /** Seconds before trying to multiply */
  spreadAfter?: number;
  /**
   * Refuge: while this Prayer abides on Will, restore this much Integrity / sec
   * (Secret Place shelter). Stacks soft-cap in Game.
   */
  refugeRegen?: number;
}

/** One intensity of a lie family (III full → I residual). */
export interface LieStage {
  level: 1 | 2 | 3;
  /** Optional rename at this intensity */
  name?: string;
  claim: string;
  verbs: VerbKit;
}

export interface LieDef {
  id: string;
  /** Base name (stages may override) */
  name: string;
  type: LieType;
  /** Full → residual stages (usually 3, 2, 1) */
  stages: LieStage[];
  /** Soft cap of this lie id on the board */
  maxOnBoard?: number;
}

export interface VerseDef {
  id: string;
  reference: string;
  textKjv: string;
  /**
   * Heart of the verse spoken during Proclaim (may be shortened).
   * Full KJV is shown in the fullscreen study panel via textKjv.
   */
  proclaimText?: string;
  /**
   * Seconds the mouth is occupied (speech resource).
   * Designed band, not raw KJV length.
   */
  speakSeconds?: number;
  category: Category;
  kind: CardKind;
  /**
   * Short “when to use” tip for the fullscreen study panel (right frame).
   * Rules detail comes from keyword pills; this is tactical guidance only.
   */
  effectSummary: string;
  respectMask: RespectMask;
  /** Preferred layer for auto place / layerPrefer */
  preferLayer?: LayerId;
  /** Instant targeting */
  selector?: InstantSelector;
  priorityTypes?: LieType[];
  /** Lie types this verse especially meets (extra level drop) */
  strongVs?: LieType[];
  /**
   * Single-target Answer ladder (snipes). layerChip ignores this.
   * - soften: always base steps only (usually −1) — targeted chip to open Redeem
   * - micro: short/narrow; meet + perfect aim can step up to 3 (full III silence)
   * - standard: base 1, meet → 2; no aim→3
   * - broad: same depth as standard; wider strongVs / forgiving selector (pay in speak time)
   */
  snipeTier?: "soften" | "micro" | "standard" | "broad";
  /**
   * Instant: base levels to step a lie down (almost always 1 for snipes).
   * Meet / aim bonuses stack on top (see answerStepCount).
   * layerChip: flat amount, no meet-clear.
   */
  answerLevels?: number;
  /** Instant: slam layer tilt toward Truth (negative delta) */
  tiltSlam?: number;
  /**
   * layerChip / layerOnly: force this layer (Mind-only tools, etc.).
   * Drag to other layers is ignored.
   */
  lockLayer?: LayerId;
  /** layerChip: only chip these lie types (omit = every lie on the layer) */
  chipOnlyTypes?: LieType[];
  /** Instant: restore integrity */
  restoreIntegrity?: number;
  /**
   * Instant (self): for a duration, slowly contest matching enemy lies
   * (wear toward level step-downs). Soft multi-target control — not a wipe.
   */
  joyField?: {
    /** Seconds the field lasts */
    duration: number;
    /** Wear applied per second to each matching lie (1.0 ≈ one level step) */
    wearPerSec: number;
    /** Lie types worn (default: fear) */
    onlyTypes?: LieType[];
    /** Mild advance slow while the field is up (1 = none; Still uses ~0.3) */
    slowFactor?: number;
    /** Cap level steps this cast can deal per lie (default 1) */
    maxStepsPerLie?: number;
  };
  /**
   * Instant: after play, draw this many extra verses into hand
   * (may exceed handSize by this amount once).
   */
  handDraw?: number;
  /**
   * Instant: next N proclaims are shorter (speech haste).
   * factor 0.75 = 25% faster speak; charges default 1.
   */
  speakHaste?: { factor: number; charges?: number };
  /**
   * Lay aside: after play, put another hand card on the used pile and draw.
   * Drop onto a hand card to choose; otherwise auto-picks a situational brick.
   */
  layAside?: boolean;
  /**
   * Name: on Dogma, light the type chip (demand) and Still briefly. No peel.
   * James 1:5 also peels if Wisdom is the current demand.
   */
  nameLie?: boolean;
  /** Silence every lieType === confusion on the board. */
  wipeConfusion?: boolean;
  /** Rider: peel/silence a Dogma core if one is on the board. */
  castDown?: boolean;
  /** Instant: seconds of slow on target lie or whole layer */
  slowDuration?: number;
  /** Instant: multiplier on advance windup while slowed */
  slowFactor?: number;
  /**
   * Restore Keep rider: after mending the target Prayer, fortify it
   * so incoming wear is reduced for a short time (“not weary”).
   */
  fortifyAfterRestore?: {
    duration: number;
    /** Incoming wear multiplier while fortified (default 0.5) */
    wearFactor?: number;
  };
  /**
   * Restore Keep rider: lightly renew other Prayers on the same layer
   * (clear wear; optional levels restored toward max).
   */
  mendAdjacent?: {
    clearWear?: boolean;
    /** Levels restored toward each neighbor’s max (default 0) */
    levels?: number;
  };
  /** Convert: prayer name when redeeming a lie */
  convertPrayerName?: string;
  /**
   * Convert: intensity steps relative to the redeemed lie (default 0).
   * 0 = same level (weak redeem); 1 = one step stronger when possible (strong).
   * Against a III lie with bonus ≥ 1, prayer is III with fortified verbs.
   */
  convertPrayerBonus?: number;
  /**
   * Empower: how many subsequent Answer/Chip plays gain the bonus (default 1).
   */
  empowerCharges?: number;
  /**
   * Empower: extra Answer/Chip levels granted per charge (default 1).
   * Soft-capped with normal Answer math (see Game.consumeEmpowerOnAnswer).
   */
  empowerBonusSteps?: number;
  /** Placeable presence */
  placeable?: {
    name: string;
    /** Starting intensity 1–3 */
    level: number;
    verbs: VerbKit;
  };
}

/** Instants that can spend an Empower charge for +step. */
export function isEmpowerableInstant(def: VerseDef): boolean {
  if (def.kind !== "instant") return false;
  const sel = def.selector ?? "strongestLie";
  if (
    sel === "empower" ||
    sel === "restorePrayer" ||
    sel === "gatherPrayers" ||
    sel === "self" ||
    sel === "convertLie" ||
    sel === "slowLayer"
  ) {
    return false;
  }
  if (sel === "slowLie") return (def.answerLevels ?? 0) > 0;
  if (
    sel === "strongestLie" ||
    sel === "innermostLie" ||
    sel === "typePriority" ||
    sel === "layerChip" ||
    sel === "layerSlam"
  ) {
    return true;
  }
  // Default single-target Answer path
  return !!def.answerLevels;
}

export interface CardInstance {
  instanceId: string;
  def: VerseDef;
  /** Just dealt into hand — UI plays deal-in animation once */
  justDrawn?: boolean;
}

/** Short-lived visual cue for BoardUi / CSS */
export type PresenceAnim = "spawn" | "push" | "arrive" | "siege" | "multiply";

/** Who last answered / wore this lie (for truth-on-silence). */
export interface TouchAttribution {
  kind: "verse" | "prayer";
  defId: string;
  reference: string;
  line: string;
}

/** Themed presentation for a lie spawn (campaign chapters, etc.). */
export interface LieSpawnSkin {
  /** Display name at full intensity (level 3); stages may override via names */
  name?: string;
  /** Per-intensity names (1 residual … 3 full) */
  names?: Partial<Record<1 | 2 | 3, string>>;
  /** Full-intensity claim; used when claims[3] omitted */
  claim?: string;
  /** Per-intensity claim ladder */
  claims?: Partial<Record<1 | 2 | 3, string>>;
  /**
   * Art plate family under `/art/lies/{artId}-{1|2|3}.webp`.
   * Falls back to mechanical lie id when plates are missing.
   */
  artId?: string;
  /**
   * Claim VO key under `public/audio/vo/lies/{voId}-{level}`.
   * Unique per claim ladder (not shared across skins that reuse art).
   * Falls back to artId, then mechanical lie id.
   */
  voId?: string;
}

export interface Presence {
  instanceId: string;
  side: Side;
  /** lie def id or verse def id */
  defId: string;
  name: string;
  /** Face-up meta line */
  meta: string;
  /** Intensity 1–3; 0 = pending removal */
  level: number;
  maxLevel: number;
  /** 0..1 progress toward next step-down */
  wear: number;
  /** Current claim (lies) or short keep line (prayers) */
  claim: string;
  age: number;
  advanceTimer: number;
  spreadTimer: number;
  /**
   * Seconds remaining before this unit may start advancing / opportunistic moves.
   * Set on spawn and after each successful layer move.
   */
  dwell: number;
  /** 0..1 windup toward next layer (drives push animation) */
  advanceProgress: number;
  /** Seconds accrued toward a Secret Place accusation (`verbs.taxAfter`). */
  taxTimer: number;
  /** 0..1 windup for the Secret Place arrow */
  taxProgress: number;
  /** For lies */
  lieType?: LieType;
  /**
   * When a residual lie morphed into Confusion: original family id
   * (for art + twisted claim seed).
   */
  sourceLieId?: string;
  /** Art plate family override (campaign skins); survives confusion morph. */
  artId?: string;
  /** Claim VO key (`vo/lies/{voId}-{level}`); survives multiply / confusion. */
  voId?: string;
  /** Themed claim ladder; reapplied on each intensity step. */
  claimSkin?: Partial<Record<1 | 2 | 3, string>>;
  /** Themed name ladder; reapplied on each intensity step. */
  nameSkin?: Partial<Record<1 | 2 | 3, string>>;
  /** Seconds lived as a residual (I) toward Confusion morph. */
  confuseTimer?: number;
  /** For player placeables */
  category?: Category;
  verbs: VerbKit;
  /** Spread copies count from this lineage */
  spreadGen: number;
  /**
   * Preferred column (0–2) for this presence — keep lane stable when advancing.
   * Set on spawn; does not wander sideways.
   */
  homeSlot: number;
  /**
   * Multiply spawnlings: more eager advance once dwell/corruption allow,
   * but stay in lane (no side-shuffling).
   */
  marching: boolean;
  anim: PresenceAnim | null;
  /** Seconds remaining for `anim` class */
  animTime: number;
  /** Seconds remaining of Slow (advance windup reduced) */
  slowTimer: number;
  /** Multiplier on advance fill while slowed */
  slowFactor: number;
  /**
   * Seconds remaining of Renew fortify (player Prayers): reduced incoming wear.
   */
  fortifyTimer: number;
  /** Incoming wear multiplier while fortifyTimer > 0 (e.g. 0.5 = half wear) */
  fortifyWearFactor: number;
  /** Last Word that touched this lie (truth-on-silence) */
  lastTouch?: TouchAttribution;
  /**
   * Doubt onion (only when lieType === "doubt").
   * Outer shells convert/revert/tangle; core finishes via direct Answer or Redeem.
   * Core-only (outerCount 0) is Redeemable immediately; wrong Word grows shells.
   * `level` tracks remaining Doubt intensity (outers open + core).
   */
  doubt?: {
    outerCount: number;
    maxOuterCount: number;
    outerFaith: number;
    decayAcc: number;
    decayEnabled: boolean;
  };
  /**
   * Dogma peel (only when lieType === "dogma").
   * Typed shells; claim changes on peel; chip never peels.
   */
  dogma?: {
    index: number;
    sequence: Category[];
    named: boolean;
    scaffold: "teach" | "full";
    shellHp: number;
  };
}

export interface DragIntent {
  layer: LayerId;
  slot: number;
  /**
   * Enemy presence the player was aiming at (by instance id).
   * Preferred over layer/slot so a lie that advances mid-drag still gets hit.
   */
  presenceId?: string;
  /** Lay aside: hand card instance to put on the used pile. */
  layAsideInstanceId?: string;
}

export function levelRoman(level: number): string {
  if (level >= 3) return "III";
  if (level === 2) return "II";
  if (level === 1) return "I";
  return "—";
}

/** Stage for a lie at a given intensity (falls back to lowest). */
export function lieStageAt(
  def: LieDef,
  level: number,
): LieStage {
  const sorted = [...def.stages].sort((a, b) => b.level - a.level);
  const exact = sorted.find((s) => s.level === level);
  if (exact) return exact;
  return sorted[sorted.length - 1] ?? sorted[0]!;
}

export function lieMaxLevel(def: LieDef): number {
  return Math.max(...def.stages.map((s) => s.level), 1);
}
